<script setup lang="ts">
import type { Organization, Homepage, OrganizationMember } from '@/stores/admin'
import { useAdmin } from '@/stores/admin'
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import type { Analysis } from '@/stores/dashboards'
import AdminAnalysisDashInputs from '@/views/forms/AdminAnalysisDashInputs.vue'
import { organizationAnalysisFormSchema } from '@/views/forms/schema.ts'
import { formatIconForStorage } from '@/lib/iconify'
import LucideIconPicker from '@/components/LucideIconPicker.vue'
import { handleError } from '@/lib/error'
import type { FormError } from '@nuxt/ui'

const route = useRoute()
const admin = useAdmin()
const toast = useToast()

if (admin.organizations.length === 0) {
  await admin.fetchOrganizations()
}

const organization = ref<Organization | undefined>(
  admin.organizations.find((org: Organization) => org.slug === (route.params.slug as string)),
)

// Loading and saving states
const isLoading = ref(true)
const isSaving = ref(false)
const isInitialized = ref(false)

// Original data (for change detection)
const originalAnalyses = ref<Analysis[]>([])
const originalHomepage = ref<Homepage | null>(null)

// Org members for access control
const orgMembers = ref<OrganizationMember[]>([])

// Current form state
const homepageEnabled = ref(false)
const notionPageId = ref('')
const notionHost = ref('https://ginjer-ai.notion.site')
const reportsEnabled = ref(false)
const originalReportsEnabled = ref(false)

// Form state for analyses (editable)
type DashboardFormItem = {
  title: string
  dashboard_url: string
  allowed_member_ids: string[] | null
  is_restricted: boolean
}
type AnalysisFormItem = {
  title: string
  icon: string
  dashboards: DashboardFormItem[]
  allowed_member_ids: string[] | null
  is_restricted: boolean
}
const formAnalyses = ref<AnalysisFormItem[]>([])
const validationErrors = ref<FormError[]>([])

// Expanded analysis for editing dashboards
const expandedAnalysisIndex = ref<number | null>(null)

function formatAnalysesForForm(analyses: Analysis[]): AnalysisFormItem[] {
  return [...analyses]
    .sort((a, b) => a.menu_position - b.menu_position)
    .map((analysis) => ({
      title: analysis.title,
      icon: analysis.icon || '',
      dashboards: analysis.dashboards.map((dashboard) => ({
        title: dashboard.title,
        dashboard_url: dashboard.dashboard_url,
        allowed_member_ids: dashboard.allowed_member_ids ?? null,
        is_restricted:
          dashboard.allowed_member_ids !== null && dashboard.allowed_member_ids !== undefined,
      })),
      allowed_member_ids: analysis.allowed_member_ids ?? null,
      is_restricted:
        analysis.allowed_member_ids !== null && analysis.allowed_member_ids !== undefined,
    }))
}

function addAnalysis() {
  formAnalyses.value.push({
    title: '',
    icon: '',
    dashboards: [{ title: '', dashboard_url: '', allowed_member_ids: null, is_restricted: false }],
    allowed_member_ids: null,
    is_restricted: false,
  })
}

function removeAnalysis(index: number) {
  formAnalyses.value.splice(index, 1)
  if (expandedAnalysisIndex.value === index) {
    expandedAnalysisIndex.value = null
  } else if (expandedAnalysisIndex.value !== null && expandedAnalysisIndex.value > index) {
    expandedAnalysisIndex.value--
  }
}

function moveAnalysis(index: number, direction: 'up' | 'down') {
  const newIndex = direction === 'up' ? index - 1 : index + 1
  if (newIndex < 0 || newIndex >= formAnalyses.value.length) return
  const item = formAnalyses.value.splice(index, 1)[0]
  formAnalyses.value.splice(newIndex, 0, item)
  if (expandedAnalysisIndex.value === index) {
    expandedAnalysisIndex.value = newIndex
  } else if (expandedAnalysisIndex.value === newIndex) {
    expandedAnalysisIndex.value = index
  }
}

function toggleRestriction(index: number) {
  const analysis = formAnalyses.value[index]
  analysis.is_restricted = !analysis.is_restricted
  if (!analysis.is_restricted) {
    analysis.allowed_member_ids = null
  } else {
    analysis.allowed_member_ids = []
  }
}

function formatMemberNames(memberIds: string[] | null): string {
  if (!memberIds || memberIds.length === 0) return ''
  const names = memberIds
    .map((id) => {
      const member = orgMembers.value.find((m) => m.member_id === id)
      if (!member) return null
      return member.name?.split(' ')[0] || member.email.split('@')[0]
    })
    .filter(Boolean) as string[]
  if (names.length === 0) return 'Unknown'
  if (names.length <= 2) return names.join(', ')
  return `${names[0]} + ${names.length - 1} more`
}

const memberOptions = computed(() =>
  orgMembers.value
    .filter((m) => m.status === 'active' || m.status === 'invited')
    .map((m) => ({
      label: m.name || m.email,
      value: m.member_id,
    })),
)

// Load data
if (organization.value) {
  Promise.all([
    admin.fetchOrganizationDashboards(organization.value.id),
    admin.fetchOrganizationHomepage(organization.value.id),
    // Members fetch failure shouldn't block the page
    admin.fetchOrganizationMembers(organization.value.id).catch(() => [] as OrganizationMember[]),
  ])
    .then(([dashboardsResponse, homepageResponse, members]) => {
      originalAnalyses.value = dashboardsResponse.analyses
      formAnalyses.value = formatAnalysesForForm(dashboardsResponse.analyses)
      if (formAnalyses.value.length === 0) {
        addAnalysis()
      }

      originalHomepage.value = homepageResponse
      homepageEnabled.value = !!homepageResponse?.notion_page_id
      notionPageId.value = homepageResponse?.notion_page_id || ''
      notionHost.value = homepageResponse?.notion_host || 'https://ginjer-ai.notion.site'
      reportsEnabled.value = organization.value?.reports_enabled ?? false
      originalReportsEnabled.value = organization.value?.reports_enabled ?? false

      orgMembers.value = members
    })
    .catch((error) => {
      handleError(error, 'Failed to load content')
    })
    .finally(() => {
      isLoading.value = false
      isInitialized.value = true
    })
} else {
  isLoading.value = false
}

// Change detection
const hasChanges = computed(() => {
  if (!isInitialized.value) return false

  const originalEnabled = !!originalHomepage.value?.notion_page_id
  const originalPageId = originalHomepage.value?.notion_page_id || ''

  if (homepageEnabled.value !== originalEnabled) return true
  if (homepageEnabled.value && notionPageId.value !== originalPageId) return true

  const filterEmpty = (items: AnalysisFormItem[]) =>
    items.filter((a) => a.title.trim() !== '' || a.dashboards.some((d) => d.title.trim() !== ''))
  const originalFormatted = formatAnalysesForForm(originalAnalyses.value)
  if (
    JSON.stringify(filterEmpty(formAnalyses.value)) !==
    JSON.stringify(filterEmpty(originalFormatted))
  )
    return true

  // Check reports_enabled changes
  if (reportsEnabled.value !== originalReportsEnabled.value) return true

  return false
})

// Save all changes
async function saveAll() {
  if (!organization.value || isSaving.value) return

  const result = organizationAnalysisFormSchema.safeParse({ analyses: formAnalyses.value })
  if (!result.success) {
    validationErrors.value = result.error.issues.map((issue) => ({
      name: issue.path.join('.'),
      message: issue.message,
    }))
    toast.add({
      title: 'Validation error',
      description: 'Please fix the highlighted errors before saving.',
      color: 'error',
    })
    return
  }
  validationErrors.value = []

  isSaving.value = true

  try {
    const analysesData = formAnalyses.value.map((analysis, aIndex) => ({
      title: analysis.title,
      menu_position: aIndex + 1,
      icon: formatIconForStorage(analysis.icon),
      dashboards: analysis.dashboards.map((dashboard) => ({
        title: dashboard.title,
        dashboard_url: dashboard.dashboard_url,
        // null = inherits from analysis, [] = admins only, [...] = specific members
        allowed_member_ids: dashboard.is_restricted ? dashboard.allowed_member_ids : null,
      })),
      // null = all members, [] = admins only, [...] = specific members
      allowed_member_ids: analysis.is_restricted ? analysis.allowed_member_ids : null,
    }))
    await admin.updateOrganizationDashboards(organization.value.id, analysesData)

    if (homepageEnabled.value && notionPageId.value) {
      const result = await admin.updateOrganizationHomepage(
        organization.value.id,
        notionPageId.value,
      )
      originalHomepage.value = result
    } else if (!homepageEnabled.value && originalHomepage.value?.notion_page_id) {
      await admin.deleteOrganizationHomepage(organization.value.id)
      originalHomepage.value = null
    }

    // Save reports_enabled if changed
    if (reportsEnabled.value !== originalReportsEnabled.value) {
      await admin.updateOrganization(organization.value.id, {
        reports_enabled: reportsEnabled.value,
      })
      originalReportsEnabled.value = reportsEnabled.value
    }

    const dashboardsResponse = await admin.fetchOrganizationDashboards(organization.value.id)
    originalAnalyses.value = dashboardsResponse.analyses
    formAnalyses.value = formatAnalysesForForm(dashboardsResponse.analyses)
  } catch (error) {
    handleError(error, 'Failed to save changes')
  } finally {
    isSaving.value = false
  }
}

function addDashboard(analysisIndex: number) {
  formAnalyses.value[analysisIndex].dashboards.push({
    title: '',
    dashboard_url: '',
    allowed_member_ids: null,
    is_restricted: false,
  })
}

function toggleDashboardRestriction(analysisIndex: number, dashboardIndex: number) {
  const dashboard = formAnalyses.value[analysisIndex].dashboards[dashboardIndex]
  dashboard.is_restricted = !dashboard.is_restricted
  if (!dashboard.is_restricted) {
    dashboard.allowed_member_ids = null
  } else {
    dashboard.allowed_member_ids = []
  }
}
</script>

<template>
  <AppComponent
    id="admin-organization-content"
    :title="organization ? `Content: ${organization?.name}` : 'Content'"
  >
    <!-- Floating save button -->
    <div v-if="!isLoading && organization" class="fixed top-4 right-6 z-50">
      <UButton
        color="primary"
        :loading="isSaving"
        :disabled="!hasChanges"
        :variant="hasChanges ? 'solid' : 'soft'"
        :class="{ 'animate-pulse': hasChanges }"
        @click="saveAll"
      >
        {{ hasChanges ? 'Save changes' : 'Saved' }}
      </UButton>
    </div>

    <div class="flex flex-col gap-2 sm:gap-4 lg:gap-4 w-full lg:max-w-4xl mx-auto">
      <div v-if="isLoading" class="space-y-6">
        <USkeleton class="h-32 w-full" />
        <USkeleton class="h-48 w-full" />
      </div>

      <template v-else-if="organization">
        <!-- Features Section -->
        <UPageCard
          title="Features"
          description="Enable or disable organization-level features."
          variant="naked"
          class="mb-4"
        />
        <UPageCard variant="subtle" class="mb-8">
          <LabeledSwitch
            v-model="reportsEnabled"
            label="Reports"
            description="Enable access to Reports (reports.ginjer.ai)"
          />
        </UPageCard>

        <!-- Homepage Section -->
        <UPageCard
          title="Homepage configuration"
          description="Embed a Notion page as the organization's homepage."
          variant="naked"
          class="mb-4"
        />
        <UPageCard variant="subtle" class="mb-8">
          <div class="space-y-4">
            <LabeledSwitch v-model="homepageEnabled" label="Enable Notion homepage" />
            <div v-if="homepageEnabled" class="space-y-4">
              <UFormField
                label="Notion Page ID"
                description="To get the ID: open your Notion page → Share → Embed this page → copy the ID from the URL."
                class="flex max-sm:flex-col justify-between items-start gap-4"
              >
                <UFieldGroup class="items-start">
                  <UInput v-model="notionPageId" placeholder="1a682ab5188980899564c2a9152bc5e3" />
                  <UButton
                    :to="notionPageId ? `${notionHost}/ebd//${notionPageId}` : undefined"
                    target="_blank"
                    icon="i-lucide-external-link"
                    variant="subtle"
                    :disabled="!notionPageId"
                  />
                </UFieldGroup>
              </UFormField>
            </div>
          </div>
        </UPageCard>

        <!-- Analyses Section -->
        <UPageCard
          title="Analyses"
          description="Configure analyses and their dashboards. Each analysis appears in the sidebar menu."
          variant="naked"
          class="mb-4"
        />

        <UPageCard variant="subtle">
          <UForm
            :schema="organizationAnalysisFormSchema"
            :state="{ analyses: formAnalyses }"
            :errors="validationErrors"
          >
            <!-- Analysis rows -->
            <div class="space-y-2">
              <div
                v-for="(analysis, index) in formAnalyses"
                :key="index"
                class="border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <!-- Main row -->
                <div class="flex items-center gap-3 p-3">
                  <!-- Icon picker -->
                  <LucideIconPicker v-model="formAnalyses[index].icon" />

                  <!-- Title -->
                  <UInput
                    v-model="formAnalyses[index].title"
                    placeholder="Analysis title"
                    class="flex-1"
                  />

                  <!-- Dashboards count -->
                  <UButton
                    variant="ghost"
                    color="neutral"
                    size="sm"
                    @click="expandedAnalysisIndex = expandedAnalysisIndex === index ? null : index"
                  >
                    {{ analysis.dashboards.length }} dashboard{{
                      analysis.dashboards.length !== 1 ? 's' : ''
                    }}
                    <UIcon
                      :name="
                        expandedAnalysisIndex === index
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="size-4"
                    />
                  </UButton>

                  <!-- Access control -->
                  <div class="flex items-center gap-2">
                    <USelectMenu
                      :model-value="formAnalyses[index].allowed_member_ids ?? []"
                      @update:model-value="
                        (v: string[]) => {
                          formAnalyses[index].allowed_member_ids = v
                          formAnalyses[index].is_restricted = true
                        }
                      "
                      :items="memberOptions"
                      value-key="value"
                      multiple
                      :placeholder="
                        analysis.is_restricted
                          ? analysis.allowed_member_ids?.length
                            ? formatMemberNames(analysis.allowed_member_ids)
                            : 'No one'
                          : 'All members'
                      "
                      class="w-40"
                    />
                    <UButton
                      v-if="analysis.is_restricted"
                      icon="i-lucide-x"
                      size="xs"
                      variant="ghost"
                      color="neutral"
                      title="Reset to all members"
                      @click="toggleRestriction(index)"
                    />
                  </div>

                  <!-- Actions -->
                  <div class="flex items-center gap-1">
                    <UButton
                      icon="i-lucide-chevron-up"
                      variant="ghost"
                      color="neutral"
                      size="xs"
                      :disabled="index === 0"
                      @click="moveAnalysis(index, 'up')"
                    />
                    <UButton
                      icon="i-lucide-chevron-down"
                      variant="ghost"
                      color="neutral"
                      size="xs"
                      :disabled="index === formAnalyses.length - 1"
                      @click="moveAnalysis(index, 'down')"
                    />
                    <UButton
                      icon="i-lucide-trash-2"
                      variant="ghost"
                      color="error"
                      size="xs"
                      @click="removeAnalysis(index)"
                    />
                  </div>
                </div>

                <!-- Expanded dashboards -->
                <div
                  v-if="expandedAnalysisIndex === index"
                  class="p-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700"
                >
                  <div class="space-y-3">
                    <div
                      v-for="(dashboard, dIdx) in analysis.dashboards"
                      :key="`dashboards.${dIdx}`"
                      class="flex items-center gap-2"
                    >
                      <AdminAnalysisDashInputs
                        :name="`analyses.${index}.dashboards.${dIdx}`"
                        :dashboard="dashboard"
                        :removeDashboard="() => analysis.dashboards.splice(dIdx, 1)"
                        class="flex-1"
                      />
                      <!-- Dashboard access control (subset of analysis members) -->
                      <USelectMenu
                        :model-value="dashboard.allowed_member_ids ?? []"
                        @update:model-value="
                          (v: string[]) => {
                            dashboard.allowed_member_ids = v
                            dashboard.is_restricted = true
                          }
                        "
                        :items="
                          analysis.is_restricted
                            ? memberOptions.filter((m) =>
                                analysis.allowed_member_ids?.includes(m.value),
                              )
                            : memberOptions
                        "
                        value-key="value"
                        multiple
                        :placeholder="
                          dashboard.is_restricted
                            ? dashboard.allowed_member_ids?.length
                              ? 'Limited'
                              : 'No one'
                            : 'Inherits'
                        "
                        size="xs"
                        class="w-28"
                      />
                      <UButton
                        v-if="dashboard.is_restricted"
                        icon="i-lucide-x"
                        size="xs"
                        variant="ghost"
                        color="neutral"
                        title="Reset to inherit"
                        @click="toggleDashboardRestriction(index, dIdx)"
                      />
                    </div>
                    <UButton
                      icon="i-lucide-plus"
                      variant="subtle"
                      color="info"
                      size="sm"
                      @click="addDashboard(index)"
                    >
                      Add dashboard
                    </UButton>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4">
              <UButton @click="addAnalysis" icon="i-lucide-plus" variant="subtle" color="info">
                New analysis
              </UButton>
            </div>
          </UForm>
        </UPageCard>
      </template>
    </div>
  </AppComponent>
</template>
